/** Outcome a tracked event reports. `error` is absent on purpose: it is reserved for the invoice
 * retry, where it means a network failure rather than a rejection by the PAC. */
export const AnalyticsStatuses = {
  SUCCESS: 'success',
  FAILED: 'failed',
} as const;

export type AnalyticsStatus = typeof AnalyticsStatuses[keyof typeof AnalyticsStatuses];

/** Sub-steps of the payment leg, one per wizard route. */
export const WizardSteps = {
  METHOD: 'method',
  INVOICE: 'invoice',
  CARDS: 'cards',
  CHECKOUT: 'checkout',
  CONFIRMATION: 'confirmation',
} as const;

export type WizardStep = typeof WizardSteps[keyof typeof WizardSteps];

export const TrackedPaymentMethods = {
  CARD: 'card',
  SPEI: 'spei',
} as const;

export type TrackedPaymentMethod = typeof TrackedPaymentMethods[keyof typeof TrackedPaymentMethods];

/** Surface a grouping change was made on, named as the UI names it: "Agrupar suscripciones",
 * "Editar suscripción" and "¿Separar suscripciones?". */
export const GroupingActions = {
  GROUP: 'group',
  EDIT: 'edit',
  SEPARATE: 'separate',
} as const;

export type GroupingAction = typeof GroupingActions[keyof typeof GroupingActions];

/** Whether the block ends up with a fiscal record attached or explicitly without one. */
export const InvoicingChoices = {
  INVOICE: 'invoice',
  NO_INVOICE: 'no_invoice',
} as const;

export type InvoicingChoice = typeof InvoicingChoices[keyof typeof InvoicingChoices];

/** How the user reached a fiscal record: picked one already saved, or captured a new one. */
export const InvoicingMethods = {
  EXISTING: 'existing',
  NEW: 'new',
} as const;

export type InvoicingMethod = typeof InvoicingMethods[keyof typeof InvoicingMethods];

/** Screen the invoicing choice was made on. */
export const InvoicingSources = {
  WIZARD: 'wizard',
  BLOCK_DETAIL: 'block_detail',
} as const;

export type InvoicingSource = typeof InvoicingSources[keyof typeof InvoicingSources];

/** What a retry re-stamps: only the CFDIs that failed, or the whole payment. */
export const RetryScopes = {
  FAILED_ONLY: 'failed_only',
  FULL_PAYMENT: 'full_payment',
} as const;

export type RetryScope = typeof RetryScopes[keyof typeof RetryScopes];

/** Outcome of a re-stamping. `rejected` is the PAC turning it down; `error` is everything else. */
export const RetryStatuses = {
  ISSUED: 'issued',
  REJECTED: 'rejected',
  ERROR: 'error',
} as const;

export type RetryStatus = typeof RetryStatuses[keyof typeof RetryStatuses];

/** Flow the fiscal record was edited from. */
export const InvoiceDataSources = {
  EDIT: 'edit',
  RETRY: 'retry',
} as const;

export type InvoiceDataSource = typeof InvoiceDataSources[keyof typeof InvoiceDataSources];
